import { EntityList } from '@/components/EntityList';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { queryKeys } from '@/lib/queryKeys';
import { AppVersion } from '@/pages/app-versions/types';
import { mdiCellphoneArrowDown } from '@mdi/js';
import * as React from 'react';
import { AppVersionsFilters } from './AppVersionsFilters';
import { AppVersionsTable } from './AppVersionsTable';

export interface AppVersionsListProps {
  onRowClick: (appVersion: AppVersion) => void;
  onEditAppVersion: (appVersion: AppVersion) => void;
  onDeleteAppVersion: (appVersion: AppVersion) => void;
  onCreateAppVersion: () => void;
  isDeleting: boolean;
}

export const AppVersionsList: React.FC<AppVersionsListProps> = ({
  onRowClick,
  onEditAppVersion,
  onDeleteAppVersion,
  onCreateAppVersion,
  isDeleting,
}) => {
  const listState = usePaginatedList<
    AppVersion,
    { enabled: string; platform: string }
  >({
    queryKey: queryKeys.ADMIN_APP_VERSIONS,
    endpoint: '/admin/app-versions',
    initialFilters: { enabled: '', platform: '' },
    buildQueryParams: ({ page, limit, sortBy, sortOrder, filters }) => ({
      page,
      limit,
      enabled: filters.enabled ? filters.enabled === 'true' : undefined,
      platform: filters.platform || undefined,
      sortBy: sortBy as 'platform' | 'createdAt',
      sortOrder,
    }),
  });

  return (
    <EntityList
      title='App Versions'
      description='Manage mobile app version requirements and update settings'
      icon={mdiCellphoneArrowDown}
      countIcon={mdiCellphoneArrowDown}
      countLabel={`${listState.list.total} version${listState.list.total !== 1 ? 's' : ''}`}
      createLabel='Add Version'
      listState={listState}
      onCreateClick={onCreateAppVersion}
      renderFilters={() => (
        <AppVersionsFilters
          selectedEnabled={listState.state.filters.enabled}
          selectedPlatform={listState.state.filters.platform}
          onEnabledChange={value =>
            listState.actions.setFilter('enabled', value)
          }
          onPlatformChange={value =>
            listState.actions.setFilter('platform', value)
          }
          onClearFilters={listState.actions.clearFilters}
        />
      )}
      renderTable={() => (
        <AppVersionsTable
          appVersions={listState.list.items}
          isLoading={listState.isLoading}
          sortBy={listState.state.sortBy}
          sortOrder={listState.state.sortOrder}
          onSort={listState.actions.setSort}
          onRowClick={onRowClick}
          onEditAppVersion={onEditAppVersion}
          onDeleteAppVersion={onDeleteAppVersion}
          isDeleting={isDeleting}
        />
      )}
    />
  );
};
